import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BookOpen, GraduationCap, Eye, EyeOff, KeyRound } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentLogin = () => {
  const navigate = useNavigate();
  const { login, completeLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });

  // First-login mandatory password reset
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cleanUsername = (loginData.username || '').trim();
      const cleanPassword = (loginData.password || '').trim();
      const user = await login(cleanUsername, cleanPassword);
      if (user.role !== 'student') {
        toast.error('This login is for students only. Please use teacher login.');
        return;
      }
      if (user.must_change_password) {
        // Force new-password flow on first login. Cannot proceed to dashboard until done.
        setPasswordForm({ currentPassword: cleanPassword, newPassword: '', confirmPassword: '' });
        setShowPasswordChange(true);
        return;
      }
      toast.success('Welcome back, Student!');
      navigate('/student/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (passwordForm.newPassword === passwordForm.currentPassword) {
      toast.error('New password must be different from current password');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      toast.success('Password changed! Welcome to MyClassAI');
      setShowPasswordChange(false);
      await completeLogin();
      navigate('/student/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur shadow-2xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center">
              <BookOpen className="w-12 h-12 text-purple-600" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold text-purple-900" data-testid="student-login-title">
            Student Login
          </CardTitle>
          <CardDescription className="text-lg">
            MyClassAI - Student Portal
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                placeholder="Enter your username"
                required
                data-testid="student-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                  data-testid="student-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full py-6 text-lg bg-purple-600 hover:bg-purple-700"
              disabled={loading}
              data-testid="student-login-button"
            >
              {loading ? 'Logging in...' : 'Login as Student'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-2">Are you a teacher?</p>
            <Link to="/teacher-login">
              <Button variant="outline" className="w-full">
                <GraduationCap className="w-4 h-4 mr-2" />
                Go to Teacher Login
              </Button>
            </Link>
          </div>

          <div className="mt-6 p-4 bg-purple-50 rounded-lg">
            <p className="text-xs text-purple-800 font-semibold mb-2">Demo Credentials:</p>
            <p className="text-xs text-purple-700">Username: student1</p>
            <p className="text-xs text-purple-700">Password: password123</p>
          </div>
        </CardContent>
      </Card>

      {/* Mandatory Password Change Dialog — first login only */}
      <Dialog open={showPasswordChange} onOpenChange={() => { /* prevent close */ }}>
        <DialogContent
          className="bg-white max-w-md max-h-[90vh] overflow-y-auto [&>button]:hidden"
          data-testid="student-change-password-dialog"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-900">
              <KeyRound className="w-5 h-5" />
              Set Your New Password
            </DialogTitle>
            <DialogDescription>
              This is your first login. Please set a new password to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder="At least 6 characters"
                data-testid="student-new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="Retype new password"
                data-testid="student-confirm-password-input"
              />
            </div>
            <p className="text-xs text-gray-500">
              Remember this password — you&apos;ll use it for every login. Ask your teacher if you forget.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="student-change-password-submit"
            >
              {changingPassword ? 'Saving...' : 'Set Password & Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentLogin;
